import { useQuery } from '@tanstack/react-query';
import { Link, Navigate, Outlet, useLocation } from 'react-router';

import { sessionQuery } from '@/entities/session';
import { paths } from '@/shared/config';
import { EmptyState, ErrorState, LoadingState } from '@/shared/ui/state';

/** Pages for signed-in users; the others go to the sign-in page and come back after it. */
export function RequireAuth() {
  const session = useQuery(sessionQuery);
  const location = useLocation();
  if (session.isPending) return <LoadingState />;
  if (session.isError)
    return <ErrorState error={session.error} onRetry={() => void session.refetch()} />;
  if (!session.data.authenticated) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`${paths.login}?next=${next}`} replace />;
  }
  return <Outlet />;
}

export function GuestOnly() {
  const session = useQuery(sessionQuery);
  if (session.isPending) return <LoadingState />;
  if (session.data?.authenticated) return <Navigate to={paths.home} replace />;
  return <Outlet />;
}

/** The only organization opens at once; with several, the user picks one. */
export function HomeRedirect() {
  const session = useQuery(sessionQuery);
  const viewer = session.data?.authenticated ? session.data.viewer : null;
  if (viewer?.isGlobalAdmin) return <Navigate to={paths.adminOrganizations} replace />;
  const organizations = viewer?.organizations ?? [];
  if (organizations.length === 1 && organizations[0]) {
    return <Navigate to={paths.organization(organizations[0].id)} replace />;
  }
  return <Navigate to={paths.organizations} replace />;
}

/** Platform administration; the BFF and the gateway check the ``crm_admin`` role too. */
export function RequireGlobalAdmin() {
  const session = useQuery(sessionQuery);
  if (session.data?.authenticated && session.data.viewer.isGlobalAdmin) return <Outlet />;
  return (
    <EmptyState title="Раздел доступен администраторам платформы">
      <Link to={paths.home}>На главную</Link>
    </EmptyState>
  );
}
